import { useEffect, useState, useMemo } from 'react';
import { useReportBuilderStore } from '../../../stores/reportBuilderStore';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import type { ColumnMeta } from '../../../types/reportBuilder';
import ChartView from '../ChartView';
import PivotView from '../PivotTable';
import ResultsViewToggle from '../ResultsViewToggle';
import ConditionalFormatPanel from '../ConditionalFormatPanel';
import { FIELD_COLORS } from '../fieldEncoding';
import { CircleNotch, WarningCircle, PaintBrush } from '@phosphor-icons/react';
import { evaluateFormat, type CellStyle } from '../../../utils/conditionalFormatUtils';

interface PreviewAndSaveStepProps {
  name: string;
  description: string;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
}

function formatCellValue(value: unknown, col: ColumnMeta): string {
  if (value === null || value === undefined) return '-';
  switch (col.display_format) {
    case 'percentage': return `${Number(value).toFixed(1)}%`;
    case 'currency': return `$${Number(value).toLocaleString()}`;
    case 'decimal_2': return Number(value).toFixed(2);
    case 'date_short': return value ? new Date(String(value)).toLocaleDateString() : '-';
    case 'datetime': return value ? new Date(String(value)).toLocaleString() : '-';
    default: return String(value);
  }
}

export default function PreviewAndSaveStep({ name, description, onNameChange, onDescriptionChange }: PreviewAndSaveStepProps) {
  const {
    results, queryLoading, queryError, viewMode, conditionalFormats, executeQuery,
    selectedDimensions, selectedMeasures, timeGroupings, filters, havingFilters, calculatedFields,
    windowFunctions, periodComparison, sorts, limit,
  } = useReportBuilderStore();
  const [showFormatPanel, setShowFormatPanel] = useState(false);

  // Build a fingerprint from the query-affecting state so we re-execute when the user
  // goes back, changes columns/filters/etc, and returns to Preview.
  const queryFingerprint = useMemo(() => JSON.stringify({
    dims: selectedDimensions,
    meas: selectedMeasures,
    tg: timeGroupings,
    flt: filters,
    hav: havingFilters,
    calc: calculatedFields,
    wf: windowFunctions,
    pc: periodComparison,
    srt: sorts,
    lim: limit,
  }), [selectedDimensions, selectedMeasures, timeGroupings, filters, havingFilters, calculatedFields, windowFunctions, periodComparison, sorts, limit]);

  // Always re-execute when the Preview step mounts or when query inputs change.
  // executeQuery() internally checks buildQuerySpec() and bails if null.
  useEffect(() => {
    executeQuery();
  }, [queryFingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-compute column values for conditional formatting
  const columnValues = useMemo(() => {
    if (!results?.data || !results?.columns) return {};
    const vals: Record<string, number[]> = {};
    for (const col of results.columns) {
      if (col.is_measure) {
        vals[col.key] = results.data.map(row => Number(row[col.key])).filter(v => !isNaN(v));
      }
    }
    return vals;
  }, [results?.data, results?.columns]);

  // Build format rules indexed by column
  const formatsByColumn = useMemo(() => {
    const map: Record<string, typeof conditionalFormats> = {};
    for (const rule of conditionalFormats) {
      if (!map[rule.column]) map[rule.column] = [];
      map[rule.column].push(rule);
    }
    return map;
  }, [conditionalFormats]);

  const gridColumns: GridColDef[] = useMemo(() => {
    if (!results?.columns) return [];
    return results.columns.map((col) => {
      const colRules = formatsByColumn[col.key];
      const def: GridColDef = {
        field: col.key,
        headerName: col.label,
        flex: 1,
        minWidth: 120,
        sortable: false,
        headerClassName: col.is_dimension ? 'rb-dim-header' : col.is_measure ? 'rb-meas-header' : '',
        valueFormatter: (value: unknown) => formatCellValue(value, col),
      };
      if (colRules && colRules.length > 0) {
        def.renderCell = (params: GridRenderCellParams) => {
          let merged: CellStyle = {};
          for (const rule of colRules) {
            const style = evaluateFormat(params.value, rule, columnValues[col.key] || []);
            merged = { ...merged, ...style };
          }
          return (
            <div style={{ ...merged, width: '100%', height: '100%', display: 'flex', alignItems: 'center', padding: '0 8px' }}>
              {formatCellValue(params.value, col)}
            </div>
          );
        };
      }
      return def;
    });
  }, [results?.columns, formatsByColumn, columnValues]);

  const rows = useMemo(() => {
    if (!results?.data) return [];
    return results.data.slice(0, 25).map((row, idx) => ({ id: idx, ...row }));
  }, [results?.data]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Preview & Save</h2>
        <p className="mt-1 text-sm text-gray-500">
          Name your report and preview the results before saving.
        </p>
      </div>

      {/* Name / Description form */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Report Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. Monthly Headcount Report"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Optional description"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* View toggle + formatting */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Data Preview</span>
        <div className="flex items-center gap-2">
          {results && viewMode === 'table' && (
            <button
              onClick={() => setShowFormatPanel(true)}
              className={`flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                conditionalFormats.length > 0
                  ? 'border-purple-300 bg-purple-50 text-purple-700'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <PaintBrush className="h-3.5 w-3.5" />
              Format{conditionalFormats.length > 0 && ` (${conditionalFormats.length})`}
            </button>
          )}
          {results && <ResultsViewToggle />}
        </div>
      </div>

      {/* Results preview */}
      {queryLoading ? (
        <div className="flex h-48 items-center justify-center rounded-lg border border-gray-200 bg-white">
          <CircleNotch size={32} className="animate-spin text-blue-500" />
        </div>
      ) : queryError ? (
        <div className="flex h-48 items-center justify-center rounded-lg border border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-600">
            <WarningCircle size={20} />
            <span className="text-sm">{queryError}</span>
          </div>
        </div>
      ) : results ? (
        <div>
          {viewMode === 'chart' ? (
            <ChartView />
          ) : viewMode === 'pivot' ? (
            <PivotView />
          ) : (
            <div className="h-[350px]">
              <DataGrid
                rows={rows}
                columns={gridColumns}
                hideFooter
                disableRowSelectionOnClick
                sx={{
                  [`& .rb-dim-header`]: { backgroundColor: FIELD_COLORS.dimension.headerBg },
                  [`& .rb-meas-header`]: { backgroundColor: FIELD_COLORS.measure.headerBg },
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  backgroundColor: '#fff',
                }}
              />
            </div>
          )}
          <p className="mt-2 text-xs text-gray-400">
            Showing first {rows.length} of {results.query_stats.row_count} rows
            {results.query_stats.query_time_ms > 0 && ` (${results.query_stats.query_time_ms}ms)`}
          </p>
        </div>
      ) : (
        <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
          <p className="text-sm text-gray-400">Query will run automatically...</p>
        </div>
      )}

      {/* Conditional Format Panel Modal */}
      {showFormatPanel && <ConditionalFormatPanel onClose={() => setShowFormatPanel(false)} />}
    </div>
  );
}
